import { VideoPresentationSchema } from '../../../renderer/src/presentation-contract';
import { createVideoPresentation, presentationSupportsPreset, SOCIAL_TARGETS, type PresentationTarget, type VideoPresentation } from '../../../renderer/src/video-presentation';
import { createSocialPresentation } from '../../../renderer/src/social-presentation';
import { RENDER_PRESETS, type RenderPresetId } from '../domain/render-preset';
import { compactLandscapePreference } from './compact-landscape-preference';

export const PRESENTATION_PREFERENCE_KEY = 'ride-trace.video-presentation.v2';
const LEGACY_PREFERENCE_KEY = 'ride-trace.video-presentation.v1';
const ELEVATION_DEFAULT_VERSION = 1;
export interface VideoPreference { presentation: VideoPresentation; preset: RenderPresetId; chosen: boolean }
interface SavedPreferences { target?: unknown; placements?: Record<string, unknown>; preset?: unknown; presets?: Record<string, unknown>; elevationDefaultVersion?: unknown }
export function defaultVideoPreference(target: PresentationTarget, socialSupported = true): VideoPreference {
  return { presentation: !socialSupported ? createVideoPresentation(target) : createSocialPresentation(target, 'top-title'),
    preset: target === 'none' ? 'landscape-16x9' : 'vertical-9x16', chosen: target === 'none' || !socialSupported };
}
function saved(socialSupported = true): SavedPreferences {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(socialSupported ? PRESENTATION_PREFERENCE_KEY : LEGACY_PREFERENCE_KEY) ?? '{}');
    const stored = value && typeof value === 'object' && !Array.isArray(value) ? value as SavedPreferences : {};
    return stored;
  } catch { return {}; }
}
export function readVideoPreference(target?: PresentationTarget, socialSupported = true): VideoPreference {
  const stored = saved(socialSupported);
  const selected = target ?? (stored.target === 'none' || SOCIAL_TARGETS.includes(stored.target as typeof SOCIAL_TARGETS[number])
    ? stored.target as PresentationTarget : 'instagram-reels');
  const candidate = stored.placements?.[selected] as Partial<VideoPreference> | undefined;
  const parsed = VideoPresentationSchema.safeParse(candidate?.presentation);
  if (parsed.success && parsed.data.target === selected && (selected === 'none' || parsed.data.version === (socialSupported ? 2 : 1))) {
    const value: VideoPreference = { presentation: parsed.data, preset: selected === 'none' ? 'landscape-16x9'
      : selected === 'instagram-feed' && candidate?.preset === 'social-4x5' ? 'social-4x5' : 'vertical-9x16',
    chosen: selected === 'none' || !socialSupported || candidate?.chosen === true };
    return socialSupported ? compactLandscapePreference(value) : value;
  }
  return defaultVideoPreference(selected, socialSupported);
}
/** Restores output settings by ratio while retaining previously selected placement settings. */
export function readVideoPresetPreference(preset?: RenderPresetId, socialSupported = true): VideoPreference {
  const stored = saved(socialSupported);
  const previous = readVideoPreference(undefined, socialSupported);
  const selected = preset ?? (RENDER_PRESETS.some(value => value.id === stored.preset) ? stored.preset as RenderPresetId : previous.preset);
  const candidate = stored.presets?.[selected] as Partial<VideoPreference> | undefined;
  const parsed = VideoPresentationSchema.safeParse(candidate?.presentation);
  if (parsed.success && candidate?.preset === selected && presentationSupportsPreset(parsed.data, selected)
    && (selected === 'landscape-16x9' ? parsed.data.target === 'none' : parsed.data.target !== 'none' && parsed.data.version === (socialSupported ? 2 : 1))) {
    const value: VideoPreference = { presentation: parsed.data, preset: selected,
      chosen: selected === 'landscape-16x9' || !socialSupported || candidate.chosen === true };
    return socialSupported ? compactLandscapePreference(value) : value;
  }
  if (previous.preset === selected) return previous;
  const target = selected === 'landscape-16x9' ? 'none' : selected === 'social-4x5' ? 'instagram-feed' : 'instagram-reels';
  return { ...readVideoPreference(target, socialSupported), preset: selected };
}

export function saveVideoPreferences(value: VideoPreference, placements: Record<string, VideoPreference>, socialSupported = true,
  presets: Record<string, VideoPreference> = {}): void {
  try { localStorage.setItem(socialSupported ? PRESENTATION_PREFERENCE_KEY : LEGACY_PREFERENCE_KEY, JSON.stringify({ target: value.presentation.target,
    preset: value.preset, presets: { ...saved(socialSupported).presets, ...presets, [value.preset]: value },
    ...(socialSupported ? { elevationDefaultVersion: ELEVATION_DEFAULT_VERSION } : {}),
    placements: { ...saved(socialSupported).placements, ...placements, [value.presentation.target]: value } })); } catch { /* Storage is optional. */ }
}
